import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { isEmpty, sortBy, uniqBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row, OverlayTrigger, Popover } from 'react-bootstrap';

import ProductCPCard from '../../Components/Card/ProductCPCard/ProductCPCard';
import { pageSize, RightsAccess } from '../../Components/helper';
import * as action from './store/ProductCP.action';
import Loader from '../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import * as systemMasterAction from '../SystemMasters/Store/SystemMasters.action';

function ProductCPIndex() {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation('common');
  const searchParams = new URLSearchParams(location?.search);
  const locaStates = location?.state;
  const expandSearchRefBin = useRef(null);
  const fromArch = searchParams.get('fromArchive');
  const callArchiveApi = locaStates?.fromArchive || Boolean(fromArch);
  const {
    UserDetails: { isSupplierLoggedIn, userInfo },
    oprationalCPDetails: { unarchivedCardsList, totalCards },
    Users: { logInRights, sectionAccess },
  } = useSelector((reducer) => reducer);
  const { CheckActionPermission } = useComponent();
  const { operationalCriticalPathRight, productCriticalPathRight } = RightsAccess();
  const productAddEditAccess = CheckActionPermission(logInRights, 'criticalPath', 'product', 'addEdit');
  const productDeleteAccess = CheckActionPermission(logInRights, 'criticalPath', 'product', 'delete');

  const [state, setState] = useState({
    selectedIdArray: [],
    cards: [],
    searchText: '',
    hasMore: false,
    pageIndex: 1,
    pageSize,
    archiveSection: false,
    showArchivePopup: false,
    archivedId: '',
    archiveComment: '',
    showDeletePopup: false,
    deleteId: '',
    loading: false,
    showMobileSearch: true,
    searchLoaded: false,
    subBrandAccess: false,
  });

  useEffect(() => {
    const initialFunctions = async () => {
      setState((prev) => ({
        ...prev,
        loading: true,
      }));
      await dispatch(action.clearReducer());
      const res = await dispatch(
        action.fetchUnArchiveCardData({
          pageIndex: 1,
          pageSize: state.pageSize,
          archived: !!callArchiveApi,
        }),
      );
      if (res) {
        setState((prev) => ({
          ...prev,
          loading: false,
          archiveSection: !!callArchiveApi,
        }));
      }
    };
    initialFunctions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, callArchiveApi]);

  useEffect(() => {
    const fetchLicensorAccess = async () => {
      const subBrandAccessRes = await dispatch(systemMasterAction.getSubBrandAccess());
      setState((prev) => ({ ...prev, subBrandAccess: subBrandAccessRes?.data || false }));
    };
    fetchLicensorAccess();
  }, [dispatch]);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      cards: [...unarchivedCardsList],
    }));
  }, [unarchivedCardsList]);

  useEffect(() => {
    const count = totalCards;
    const curentList = state.cards;
    if (!state.loading) {
      setState((prev) => ({
        ...prev,
        hasMore: count > curentList.length,
      }));
    }
  }, [totalCards, state.cards, state.loading]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (state.searchLoaded) {
        setState((debouncestate) => ({
          ...debouncestate,
          loading: true,
          pageIndex: 1,
          cards: [],
          selectedIdArray: [],
        }));
        const optns = {
          pageIndex: 1,
          pageSize: state.pageSize,
          searchText: state.searchText,
          archived: state.archiveSection,
        };
        await dispatch(action.clearReducer());
        const res = await dispatch(action.fetchUnArchiveCardData(optns));
        if (res) {
          setState((debstate) => ({
            ...debstate,
            loading: false,
            searchLoaded: false,
          }));
        }
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchText, dispatch, state.archiveSection]);

  const handleNextPageInScroll = async () => {
    let { pageIndex } = state;
    pageIndex += 1;
    setState((nextPagestate) => ({
      ...nextPagestate,
      pageIndex,
      loading: true,
    }));
    const params = {
      pageIndex,
      pageSize: state?.pageSize,
      searchText: state.searchText,
      archived: state.archiveSection,
    };
    const result = await dispatch(action.fetchUnArchiveCardData(params));
    if (result) {
      setState((nextPagestate) => ({
        ...nextPagestate,
        loading: false,
      }));
    }
  };

  const handleDuplicate = (item) => {
    history.push({
      pathname: `/product-cp/create/${item._id}`,
      state: { ...item },
    });
  };

  const handleEdit = (id) => {
    history.push({
      pathname: `/product-cp/create/${id}`,
      state: { fromArchive: state.archiveSection },
    });
  };

  const handlePCPTypeSelection = (basedOnPO) => {
    document.body.click();
    // Pass type selection via location.state (will be handled in AddEditProductCP)
    history.push({
      pathname: '/product-cp/create',
      state: { basedOnPO },
    });
  };

  const handlePinCard = async (item) => {
    const params = {
      ...item,
      pinned: !item?.pinned,
    };
    const { cards } = state;
    const foundIndex = cards.findIndex((card) => card?._id === item?._id);
    cards[foundIndex].pinned = !item?.pinned;
    let newCards = [];
    const sortedCards = sortBy(cards, ['pinned', 'createdAt']).reverse();
    const pinnedSortedCards = sortedCards.filter((pcpEle) => pcpEle.pinned);
    const unpinnedSortedCards = sortedCards.filter((pcpEle) => !pcpEle.pinned && !pcpEle.lastOpned);
    const lastOpnedCards = sortedCards.filter((pcpEle) => !pcpEle.pinned && pcpEle.lastOpned);
    newCards = [...pinnedSortedCards, ...lastOpnedCards, ...unpinnedSortedCards];
    const res = await dispatch(action.addProductCard(params, t));
    if (res) {
      setState((prev) => ({
        ...prev,
        cards: newCards,
      }));
    }
  };

  const handleCardExpand = (item) => {
    history.push({
      pathname: `/product-cp/details/${item?._id}`,
      state: { fromArchive: item.isArchived },
    });
  };

  const ShowunArchivecps = async () => {
    if (!state.archiveSection) return;
    setState((prev) => ({
      ...prev,
      loading: true,
    }));
    await dispatch(action.clearReducer());
    history.push('/product-cp');
    setState((prev) => ({
      ...prev,
      archiveSection: false,
      loading: false,
      searchText: '',
      selectedIdArray: [],
    }));
  };

  const toggleDeleteModal = () => {
    setState((prev) => ({
      ...prev,
      loading: false,
      showDeletePopup: false,
      deleteId: '',
      selectedIdArray: [],
    }));
  };

  const conformDelete = async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
    }));
    let params = {};
    if (state.deleteId === null) {
      params = {
        pcpIds: [...state.selectedIdArray],
        keyName: 'isDeleted',
      };
    } else {
      params = { pcpIds: [state.deleteId], keyName: 'isDeleted' };
    }
    const res = await dispatch(action.deleteCard({ ...params }, t));
    if (res) {
      const oldCards = state.cards;
      let newCards = [];
      if (state.deleteId) {
        newCards = oldCards.filter((oldEle) => oldEle?._id !== state.deleteId);
      } else {
        newCards = oldCards.filter((oldEle) => !state.selectedIdArray.includes(oldEle?._id));
      }
      setState((oldState) => ({ ...oldState, cards: newCards }));
      toggleDeleteModal();
    }
  };

  const deleteCard = (id) => {
    setState((prev) => ({
      ...prev,
      showDeletePopup: true,
      deleteId: id,
    }));
  };

  const handleArchiveCard = (id) => {
    setState((prev) => ({
      ...prev,
      showArchivePopup: true,
      archivedId: id,
    }));
  };
  const toggleArchive = () => {
    setState((prev) => ({
      ...prev,
      loading: false,
      showArchivePopup: false,
      archivedId: '',
      archiveComment: '',
      selectedIdArray: [],
    }));
  };
  const conformArchive = async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
    }));
    let archiveCardDetails = {};
    if (state.archivedId === null) {
      archiveCardDetails = {
        pcpIds: [...state.selectedIdArray],
        keyName: 'isArchived',
        isArchived: !state.archiveSection,
        archiveComment: state.archiveSection ? '' : state?.archiveComment,
      };
    } else {
      archiveCardDetails = {
        pcpIds: [state.archivedId],
        keyName: 'isArchived',
        isArchived: !state.archiveSection,
        archiveComment: state.archiveSection ? '' : state?.archiveComment,
      };
    }
    const res = await dispatch(action.deleteCard({ ...archiveCardDetails }, t));
    if (res) {
      const oldCards = state.cards;
      let newCards = [];
      if (state.archivedId) {
        newCards = oldCards.filter((oldEle) => oldEle?._id !== state.archivedId);
      } else {
        newCards = oldCards.filter((oldEle) => !state.selectedIdArray.includes(oldEle?._id));
      }
      setState((oldState) => ({ ...oldState, cards: newCards }));
      toggleArchive();
    }
  };

  const ShowArchivecps = async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
    }));
    await dispatch(action.clearReducer());
    const res = await dispatch(
      action.fetchUnArchiveCardData({
        pageIndex: 1,
        pageSize: 20,
        archived: true,
      }),
    );
    if (res) {
      setState((prev) => ({
        ...prev,
        archiveSection: true,
        loading: false,
        searchText: '',
        selectedIdArray: [],
      }));
    }
  };

  const archiveCommentChanges = (e) => {
    setState((prev) => ({
      ...prev,
      archiveComment: e?.target?.value,
    }));
  };

  const activeInActiveCard = async (item, index) => {
    let params = {};
    params = {
      pcpIds: [item?._id],
      keyName: 'isActiveInactive',
      isActive: !item?.isActive,
    };
    const res = await dispatch(action.deleteCard({ ...params }, t));
    if (res) {
      const oldCards = state.cards;
      oldCards[index].isActive = !item?.isActive;
      setState((oldState) => ({ ...oldState, cards: oldCards }));
    }
  };

  const selectedCard = (e, id) => {
    if (e?.target?.checked) {
      setState((prev) => ({
        ...prev,
        selectedIdArray: [...state.selectedIdArray, id],
      }));
    } else {
      const array = [...state.selectedIdArray];
      const updatedArray = array.filter((arrid) => arrid !== id);
      setState((prev) => ({
        ...prev,
        selectedIdArray: [...updatedArray],
      }));
    }
  };

  const handleSearch = (e) => {
    const targetValue = e.target.value;
    setState((searchState) => ({
      ...searchState,
      searchText: targetValue,
      searchLoaded: true,
    }));
  };

  const deleteMessageInarchive = () => {
    return state.deleteId === null
      ? t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.NULL_DELETE_ID')
      : t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.NOT_NULL_DELETE_ID');
  };
  const deleteMessageInUnarchive = () => {
    return state.deleteId === null
      ? t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.PRODUCT_CRITICAL_PATH_MESSAGE')
      : t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.DELETE_CRITICAL_PRODUCT_MESSAGE');
  };

  const navigateLog = () => {
    history.push('/product-cp/logs');
  };

  const handleIconBinClick = () => {
    // Add a class to the input on icon click
    expandSearchRefBin.current.classList.toggle('active-input');
  };

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.PRODUCT_CRITICAL_PATH')}</h4>
        </div>

        <div className="search-filter action-icons-container care-library-filter">
          <div ref={expandSearchRefBin} className="common-expand-search">
            <button type="button" onClick={handleIconBinClick} className="btn btn-toggle">
              <span className="sr-only ">{t('USER_INSTRUCTION_LIBRARY.USER_INSTRUCTION.SEARCH USER_INSTRUCTION')}</span>
              <span className="icon-Magnifying-Glass-Solid" />
            </button>
            <input
              type="text"
              className="form-control"
              value={state?.searchText}
              onChange={(e) => handleSearch(e)}
              autoComplete="off"
              placeholder={
                state.archiveSection
                  ? t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.ARCHIVED')
                  : t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.NOT_ARCHIVED')
              }
            />
          </div>
        </div>
      </div>

      <div className="floting-actions">
        {!isSupplierLoggedIn && (
          <CustomTooltip placement="bottom" tooltipText={t('RANGE_PLANS.PRODUCT_LIST.LOGS')}>
            <button aria-label="logs" type="button" onClick={navigateLog} className="btn ">
              <i className="icon-Preview-Log" />
            </button>
          </CustomTooltip>
        )}
        {productAddEditAccess && !isSupplierLoggedIn && (
          <>
            {!state.archiveSection && (
              <OverlayTrigger
                placement="top"
                trigger="click"
                rootClose
                overlay={
                  <Popover className="custom-popover">
                    <Popover.Content>
                      <div className="p-3 d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center ">
                          <p className="custom-radio mb-0">
                            <input
                              type="radio"
                              id="product-based"
                              name="pcpType"
                              value="false"
                              checked={false}
                              onChange={() => handlePCPTypeSelection(false)}
                              aria-label={t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.PRODUCT_BASED')}
                            />
                            <CustomTooltip placement="top" tooltipText={t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.PRODUCT_BASED_TOOLTIP')}>
                              <label htmlFor="product-based">{t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.PRODUCT_BASED')}</label>
                            </CustomTooltip>
                          </p>
                        </div>
                        <div className="d-flex align-items-center">
                          <p className="custom-radio mb-0">
                            <input
                              type="radio"
                              id="po-based"
                              name="pcpType"
                              value="true"
                              checked={false}
                              onChange={() => handlePCPTypeSelection(true)}
                              aria-label={t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.PO_BASED')}
                            />
                            <CustomTooltip placement="top" tooltipText={t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.PO_BASED_TOOLTIP')}>
                              <label htmlFor="po-based">{t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.PO_BASED')}</label>
                            </CustomTooltip>
                          </p>
                        </div>
                      </div>
                    </Popover.Content>
                  </Popover>
                }
              >
                <span>
                  <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.LIBRARY_FILTER.ADD')}>
                    <button type="button" className="btn" disabled={state.archiveSection || state.loading} aria-label={t('CARE_LABELS.LIBRARY_FILTER.ADD')}>
                      <i className="icon-add" />
                    </button>
                  </CustomTooltip>
                </span>
              </OverlayTrigger>
            )}

            <CustomTooltip
              placement="top"
              tooltipText={
                state.archiveSection
                  ? t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.UNARCHIVE')
                  : t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.ARCHIVE')
              }
            >
              <button
                type="button"
                aria-label="Archive"
                onClick={() => handleArchiveCard(null)}
                className="btn"
                disabled={isEmpty(state.selectedIdArray) || state.loading}
              >
                <i className="icon-Archive" />
              </button>
            </CustomTooltip>
          </>
        )}
        {productDeleteAccess && !isSupplierLoggedIn && state.archiveSection && (
          <CustomTooltip placement="top" tooltipText={t('COLOR_WAY_CARD.DELETE')}>
            <button
              type="button"
              aria-label={t('COMMON_LABEL.DELETE')}
              onClick={() => deleteCard(null)}
              className="btn"
              disabled={isEmpty(state.selectedIdArray) || state.loading}
            >
              <i className="icon-Delete" />
            </button>
          </CustomTooltip>
        )}
      </div>
      {state.loading ? (
        <Loader />
      ) : (
        <>
          <Container fluid className="d-flex flex-wrap justify-content-end my-2">
            {!isSupplierLoggedIn && sectionAccess?.criticalPath?.operationalAccess && operationalCriticalPathRight && (
              <Link className="cursor-pointer text-dark text-decoration-none btn my-1 my-md-0 btn-page-filter" to="/operational-cp">
                {t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.OPEN_OPERATIONAL_CP')}
              </Link>
            )}
            {sectionAccess?.criticalPath?.productAccess && productCriticalPathRight && (
              <button
                type="button"
                className={`btn my-1 my-md-0 btn-page-filter ${!state.archiveSection && 'btn-active'}`}
                onClick={ShowunArchivecps}
                disabled={state.loading}
              >
                {t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.OPEN_PRODUCT_CP')}
              </button>
            )}
            {!isSupplierLoggedIn && sectionAccess?.criticalPath?.operationalAccess && operationalCriticalPathRight && (
              <Link className="cursor-pointer text-dark text-decoration-none btn my-1 my-md-0 btn-page-filter" to="/operational-cp?fromArchive=true">
                {t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.ARCHIVED_OPERATIONAL_CP')}
              </Link>
            )}
            {sectionAccess?.criticalPath?.productAccess && productCriticalPathRight && (
              <Link
                className={`cursor-pointer text-dark text-decoration-none btn my-1 my-md-0 btn-page-filter ${state.archiveSection && 'btn-active'}`}
                to="/product-cp?fromArchive=true"
                onClick={ShowArchivecps}
              >
                {t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.ARCHIVED_PRODUCT_CP')}
              </Link>
            )}
          </Container>
          {isEmpty(state.cards) && (
            <Container fluid className="py-4">
              <Row>
                <Col md="12">
                  <p className="alert alert-info override-alert-info justify-content-center align-item-center d-flex">
                    {t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.NO_PATH_FOUND_MESSAGE')}
                  </p>
                </Col>
              </Row>
            </Container>
          )}
        </>
      )}
      {!isEmpty(state?.cards) && (
        <InfiniteScroll dataLength={state?.cards?.length || 0} loader={state.loading && <Loader />} hasMore={state?.hasMore} next={handleNextPageInScroll}>
          <div className="container-fluid">
            <div className="row">
              {uniqBy(state?.cards, '_id')?.map((unArchItems, unArchIndex) => {
                return (
                  <div key={unArchItems?._id} className="col-lg-4 col-xlarge-3 OCP-Card">
                    <ProductCPCard
                      item={unArchItems}
                      index={unArchIndex}
                      selectedIds={state.selectedIdArray}
                      isSupplierLoggedIn={isSupplierLoggedIn}
                      isArchived={state.archiveSection}
                      onCheck={selectedCard}
                      handlePin={handlePinCard}
                      handleEdit={handleEdit}
                      deleteCard={deleteCard}
                      handleDuplicate={handleDuplicate}
                      handleExpand={handleCardExpand}
                      archiveCard={handleArchiveCard}
                      activeInActiveCard={activeInActiveCard}
                      productAddEditAccess={productAddEditAccess}
                      productDeleteAccess={productDeleteAccess}
                      loggedInSupplier={userInfo}
                      subBrandAccess={state.subBrandAccess}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </InfiniteScroll>
      )}
      {state.showArchivePopup && (
        <ConfirmationPopup
          onOpen={state.showArchivePopup}
          onConfirm={conformArchive}
          title={`${
            state.archiveSection
              ? t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.UNARCHIVE')
              : t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.ARCHIVE')
          } ${t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.PRODUCT_CP')}`}
          message={`${t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.ARE_U_WANT_TO')} ${
            state.archiveSection
              ? t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.UNARCHIVES')
              : t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.ARCHIVES')
          } ${t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.THE_SELECTED_CRITICAL_PATH')}`}
          toggle={toggleArchive}
          isCommentOn={!state.archiveSection}
          commentText={archiveCommentChanges}
          comment={state.archiveComment}
          loading={state.loading}
          showCommentHeader
        />
      )}

      {state.showDeletePopup && (
        <ConfirmationPopup
          onOpen={state.showDeletePopup}
          onConfirm={conformDelete}
          title={t('PRODUCT_CRITICAL_PATH.PRODUCT_CRITICAL_PATH_INDEX.DELETE_PRODUCT_CP')}
          message={state.archiveSection ? deleteMessageInarchive() : deleteMessageInUnarchive()}
          toggle={toggleDeleteModal}
          loading={state.loading}
        />
      )}
    </>
  );
}

export default ProductCPIndex;
